import { useCallback } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { applyFilter } from "@Pages/Search/data/searchPageReducer";
import { AppState } from "@Store/index";
import { useAmberSales } from "hooks/useAmberSales";
import { logAppEvent } from "@Utils/appAnalytics";
import { APP_EVENTS, APP_PAGE, FILTER_FLOW } from "@Utils/webApputils/analyticEvents";
import gtagEventEmitter from "helpers/gtagEventEmitter";
import { PAGE } from "constants/configConstants";

/**
 * Hook that encapsulates the Housing Fest (amber sale) filter toggle logic.
 *
 * Provides:
 * - `isFilterActive`: whether the Housing Fest filter is currently applied
 * - `isSaleAvailable`: whether the sale banner/pill should be shown
 * - `toggleFilter`: toggles the filter on/off (dispatches applyFilter + analytics)
 * - `handleKeyDown`: keyboard handler for accessible clickable elements
 */
const useHousingFestFilter = () => {
  const dispatch = useDispatch();

  const filters = useSelector(
    ({ searchPageData }: AppState) => searchPageData.filters,
    shallowEqual,
  );

  const { isSaleOfferAvailable } = useAmberSales({ page: "SEARCH" });

  const isFilterActive = filters?.amberSaleStatus === "true";

  const toggleFilter = useCallback(() => {
    dispatch(
      applyFilter({
        ...filters,
        amberSaleStatus: isFilterActive ? "" : "true",
      }),
    );

    logAppEvent({
      eventName: APP_EVENTS.FILTER_CTA_CLICKED,
      payload: {
        filter_name: "Housing Fest",
        filter_flow: FILTER_FLOW.QUICK_FILTER,
        screen_name: APP_PAGE.SEARCH,
      },
    });
    gtagEventEmitter({ action: "housing_fest_filter_clicked", category: PAGE.SEARCH });
  }, [dispatch, filters, isFilterActive]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        toggleFilter();
      }
    },
    [toggleFilter],
  );

  return {
    isFilterActive,
    isSaleAvailable: Boolean(isSaleOfferAvailable),
    toggleFilter,
    handleKeyDown,
  };
};

export default useHousingFestFilter;
